//@ts-nocheck
import { FC, ReactElement, useState, useEffect, useLayoutEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import stylesLogin from "../Login/Login.module.css";
import Button from "../../Components/Buttons/Button";
import Head from "../../Components/Head";
import { useGetConfig } from "../../api/config";
import { validateResetTokenApi, useConfirmPasswordReset } from "../../api/passwordReset";

const resetPasswordSchema = z
  .object({
    new_password: z
      .string()
      .min(8, { message: "Use at least 8 characters." }),
    confirm_password: z
      .string()
      .min(1, { message: "Please confirm your new password." }),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Make sure both passwords match.",
    path: ["confirm_password"],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

const ResetPassword: FC = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const uid = searchParams.get("uid") || "";
  const token = searchParams.get("token") || "";

  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [tokenErrorMessage, setTokenErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  const { data: config } = useGetConfig();
  const { mutateAsync: confirmReset, isLoading: isSubmitting } = useConfirmPasswordReset();

  const {
    register,
    handleSubmit,
    setFocus,
    watch,
    formState: { errors, isValid },
  } = useForm<ResetPasswordFormValues>({
    defaultValues: { new_password: "", confirm_password: "" },
    resolver: zodResolver(resetPasswordSchema),
    mode: "onChange",
  });

  const newPasswordValue = watch("new_password", "");
  const confirmPasswordValue = watch("confirm_password", "");

  // Apply theme variables if stored
  const themes = JSON.parse(localStorage.getItem("theme") || "null");
  useLayoutEffect(() => {
    if (themes && themes.login_background_color_primary) {
      document.documentElement.style.setProperty(
        "--login-bg-color-primary",
        themes.login_background_color_primary
      );
    }
    if (themes && themes.login_button_color) {
      document.documentElement.style.setProperty(
        "--login-button-color",
        themes.login_button_color
      );
    }
  }, [themes]);

  // Pre-validate token on mount
  useEffect(() => {
    if (!uid || !token) {
      setIsValidating(false);
      setIsTokenValid(false);
      setTokenErrorMessage("The password reset link is invalid or missing required parameters.");
      return;
    }

    let isMounted = true;
    validateResetTokenApi({ uid, token })
      .then((res) => {
        if (!isMounted) return;
        if (res?.valid) {
          setIsTokenValid(true);
        } else {
          setIsTokenValid(false);
          setTokenErrorMessage(res?.message || "This password reset link is invalid or has expired.");
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        setIsTokenValid(false);
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.token?.[0] ||
          err?.response?.data?.errors?.token?.[0] ||
          "This password reset link is invalid or has expired.";
        setTokenErrorMessage(msg);
      })
      .finally(() => {
        if (isMounted) setIsValidating(false);
      });

    return () => {
      isMounted = false;
    };
  }, [uid, token]);

  useEffect(() => {
    if (isTokenValid) {
      setFocus("new_password");
    }
  }, [isTokenValid, setFocus]);

  const onSubmit = async (values: ResetPasswordFormValues) => {
    setServerErrors([]);
    try {
      await confirmReset({
        uid,
        token,
        new_password: values.new_password,
        confirm_password: values.confirm_password,
      });
      setIsSuccess(true);
    } catch (err: any) {
      const data = err?.response?.data;
      if (data) {
        const errorList: string[] = [];
        if (Array.isArray(data.new_password)) {
          errorList.push(...data.new_password);
        } else if (typeof data.new_password === "string") {
          errorList.push(data.new_password);
        }
        if (Array.isArray(data.token)) {
          errorList.push(...data.token);
        } else if (typeof data.token === "string") {
          errorList.push(data.token);
        }
        if (Array.isArray(data.confirm_password)) {
          errorList.push(...data.confirm_password);
        }
        if (Array.isArray(data.non_field_errors)) {
          errorList.push(...data.non_field_errors);
        }
        if (data.message && errorList.length === 0) {
          errorList.push(data.message);
        }
        setServerErrors(errorList.length > 0 ? errorList : ["Failed to reset password. Please try again."]);
      } else {
        setServerErrors(["An unexpected error occurred. Please try again."]);
      }
    }
  };

  return (
    <main className={stylesLogin["entire-page"]}>
      <Head
        title={config?.data?.title ? `Reset Password | ${config?.data?.title}` : "Reset Password | Web-Twin"}
        favicon={config?.data?.favicon}
      />

      <div className={stylesLogin["login-card"]}>
        {/* Left Column */}
        <div className={stylesLogin["left-column"]}>
          <div
            className={stylesLogin["back-button"]}
            onClick={() => navigate("/login")}
            title="Return to sign in"
            role="button"
            tabIndex={0}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </div>

          {/* State 1: Validating token */}
          {isValidating && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "260px" }}>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  border: "3px solid rgba(0, 204, 247, 0.2)",
                  borderTopColor: "var(--xr-cyan, #00ccf7)",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                  marginBottom: "16px",
                }}
              />
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
              <p style={{ color: "var(--text-muted, #94a3b8)", fontSize: "14px", margin: 0 }}>
                Verifying your reset link...
              </p>
            </div>
          )}

          {/* State 2: Invalid or expired token */}
          {!isValidating && !isTokenValid && !isSuccess && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  background: "rgba(255, 51, 102, 0.15)",
                  border: "1px solid rgba(255, 51, 102, 0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ff3366",
                  marginBottom: "20px",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>

              <h1 className={stylesLogin["welcome-title"]}>Invalid or expired link</h1>
              <p className={stylesLogin["welcome-subtitle"]} style={{ marginBottom: "24px" }}>
                {tokenErrorMessage || "This password reset link is invalid or has expired."}
              </p>

              <div
                style={{
                  background: "rgba(3, 7, 18, 0.6)",
                  border: "1px solid var(--card-border)",
                  borderRadius: "10px",
                  padding: "16px",
                  fontSize: "13px",
                  color: "#94a3b8",
                  lineHeight: "1.6",
                  marginBottom: "28px",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                Password reset links can only be used once and expire after 30 minutes. Please request a new link to continue.
              </div>

              <Button
                value="Request a new link"
                btnType="button"
                onClick={() => navigate("/forgot-password")}
                className={stylesLogin["login-btn"]}
              />

              <div style={{ marginTop: "16px", textAlign: "center", width: "100%" }}>
                <Link
                  to="/login"
                  style={{
                    fontSize: "13px",
                    color: "var(--text-muted, #94a3b8)",
                    textDecoration: "none",
                  }}
                >
                  Return to sign in
                </Link>
              </div>
            </div>
          )}

          {/* State 3: Valid token - Reset form */}
          {!isValidating && isTokenValid && !isSuccess && (
            <>
              <h1 className={stylesLogin["welcome-title"]}>Create a new password</h1>
              <p className={stylesLogin["welcome-subtitle"]}>
                Choose a new password for your account.
              </p>

              {serverErrors.length > 0 && (
                <div
                  style={{
                    backgroundColor: "rgba(255, 51, 102, 0.1)",
                    border: "1px solid #ff3366",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    color: "#ff6b8b",
                    fontSize: "13px",
                    marginBottom: "20px",
                  }}
                >
                  {serverErrors.map((errMsg, idx) => (
                    <div key={idx} style={{ marginBottom: idx < serverErrors.length - 1 ? "4px" : "0" }}>
                      • {errMsg}
                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className={stylesLogin["form-container"]}>
                <div className={stylesLogin["input-group"]}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <label
                      htmlFor="new_password"
                      className={stylesLogin["input-label"]}
                      style={{ marginBottom: 0, cursor: "pointer" }}
                      onClick={() => setShowPassword((prev) => !prev)}
                      title="Toggle password visibility"
                    >
                      New password
                    </label>
                    <span
                      onClick={() => setShowPassword((prev) => !prev)}
                      style={{
                        fontSize: "12px",
                        color: "var(--xr-cyan, #00ccf7)",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </span>
                  </div>
                  <input
                    id="new_password"
                    type={showPassword ? "text" : "password"}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    className={
                      errors.new_password
                        ? stylesLogin["password-input-error"]
                        : stylesLogin.password
                    }
                    {...register("new_password")}
                  />
                  {errors.new_password && (
                    <span className={stylesLogin["password-error"]}>
                      {errors.new_password.message}
                    </span>
                  )}
                </div>

                <div className={stylesLogin["input-group"]}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <label
                      htmlFor="confirm_password"
                      className={stylesLogin["input-label"]}
                      style={{ marginBottom: 0, cursor: "pointer" }}
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      title="Toggle password visibility"
                    >
                      Confirm new password
                    </label>
                    <span
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      style={{
                        fontSize: "12px",
                        color: "var(--xr-cyan, #00ccf7)",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      {showConfirmPassword ? "Hide" : "Show"}
                    </span>
                  </div>
                  <input
                    id="confirm_password"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Re-enter your new password"
                    autoComplete="new-password"
                    className={
                      errors.confirm_password
                        ? stylesLogin["password-input-error"]
                        : stylesLogin.password
                    }
                    {...register("confirm_password")}
                  />
                  {errors.confirm_password && (
                    <span className={stylesLogin["password-error"]}>
                      {errors.confirm_password.message}
                    </span>
                  )}
                </div>

                {/* Password validation helper indicators */}
                <div style={{ marginBottom: "20px", fontSize: "12px", color: "#94a3b8" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                    <span style={{ color: newPasswordValue.length >= 8 ? "#10b981" : "#64748b" }}>
                      {newPasswordValue.length >= 8 ? "✓" : "○"}
                    </span>
                    <span>Use at least 8 characters.</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span
                      style={{
                        color:
                          confirmPasswordValue && newPasswordValue === confirmPasswordValue
                            ? "#10b981"
                            : "#64748b",
                      }}
                    >
                      {confirmPasswordValue && newPasswordValue === confirmPasswordValue ? "✓" : "○"}
                    </span>
                    <span>Make sure both passwords match.</span>
                  </div>
                </div>

                <Button
                  value={isSubmitting ? "Updating password..." : "Update password"}
                  btnType="submit"
                  className={
                    !isValid || isSubmitting
                      ? stylesLogin["login-disable-btn"]
                      : stylesLogin["login-btn"]
                  }
                  disabled={!isValid || isSubmitting}
                />
              </form>
            </>
          )}

          {/* State 4: Reset successful */}
          {isSuccess && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  background: "rgba(16, 185, 129, 0.15)",
                  border: "1px solid rgba(16, 185, 129, 0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#10b981",
                  marginBottom: "20px",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>

              <h1 className={stylesLogin["welcome-title"]}>Password updated successfully</h1>
              <p className={stylesLogin["welcome-subtitle"]} style={{ marginBottom: "28px" }}>
                Your password has been changed. Sign in with your new password to continue.
              </p>

              <Button
                value="Sign in to Web-Twin Pro"
                btnType="button"
                onClick={() => navigate("/login")}
                className={stylesLogin["login-btn"]}
              />
            </div>
          )}
        </div>

        {/* Vertical Divider */}
        <div className={stylesLogin["divider"]}></div>

        {/* Right Column */}
        <div className={stylesLogin["right-column"]}>
          <h2 className={stylesLogin["feature-title"]}>Keep your account secure</h2>

          <div className={stylesLogin["feature-list"]} style={{ marginTop: "20px" }}>
            <div className={stylesLogin["feature-item"]}>
              <div className={stylesLogin["feature-icon"]}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </div>
              <span>Choose a strong password.</span>
            </div>

            <div className={stylesLogin["feature-item"]}>
              <div className={stylesLogin["feature-icon"]}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <span>Your reset link expires after 30 minutes.</span>
            </div>

            <div className={stylesLogin["feature-item"]}>
              <div className={stylesLogin["feature-icon"]}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                  <polyline points="10 17 15 12 10 7"></polyline>
                  <line x1="15" y1="12" x2="3" y2="12"></line>
                </svg>
              </div>
              <span>You'll need to sign in with your new password after resetting it.</span>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ResetPassword;
